import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { FiAward, FiPhone, FiMail } from 'react-icons/fi';
import LoadingSpinner from '../components/LoadingSpinner';
import Avatar from '../components/Avatar';


const Leaderboard = () => {
  const { type, id } = useParams();
  
  const { data, isLoading } = useQuery(
    {queryKey:['leaderboard', type, id],
    queryFn:() => axios.get(`/api/leaderboard/${type}/${id}`).then(res => res.data)
  });

  if (isLoading) {
    return <LoadingSpinner fullScreen />;
  }

  const getMedalColor = (rank) => {
    switch (rank) {
      case 1: return 'text-yellow-500';
      case 2: return 'text-gray-400';
      case 3: return 'text-orange-600';
      default: return 'text-gray-600';
    }
  };

  const getRankDisplay = (rank) => {
    if (rank <= 3) {
      return <FiAward className={`${getMedalColor(rank)} text-2xl`} />;
    }
    return <span className="text-lg font-medium text-gray-600">#{rank}</span>;
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Leaderboard</h1>
          <p className="text-gray-600 mt-1">
            {type === 'quiz' ? `Quiz: ${data?.quizTitle}` : `Classroom: ${data?.classroomName}`}
          </p>
          {type === 'classroom' && (
            <p className="text-sm text-gray-500">
              Based on {data?.totalQuizzes} active quizzes
            </p>
          )}
        </div>
        <Link to="/classrooms" className="btn-secondary">
          Back to Classrooms
        </Link>
      </div>

      {data?.leaderboard?.length === 0 ? (
        <div className="card text-center py-12">
          <FiAward className="mx-auto text-gray-400 mb-4" size={48} />
          <p className="text-lg text-gray-600">No scores yet</p>
          <p className="text-sm text-gray-500 mt-2">
            Be the first to complete {type === 'quiz' ? 'this quiz' : 'a quiz in this classroom'}!
          </p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rank
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Student
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Score
                  </th>
                  {type === 'classroom' && (
                    <>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Quizzes Taken
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Average %
                      </th>
                    </>
                  )}
                  {type === 'quiz' && (
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Percentage
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data?.leaderboard?.map((entry, index) => (
                  <tr
                    key={index}
                    className={`${index < 3 ? 'bg-gradient-to-r from-transparent' : ''} ${
                      index === 0 ? 'to-yellow-50' : index === 1 ? 'to-gray-50' : index === 2 ? 'to-orange-50' : ''
                    }`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center justify-center w-12">
                        {getRankDisplay(entry.rank)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Avatar name={entry.name} size="small" className="mr-2" />
                        <span className="text-sm font-medium text-gray-900">
                          {entry.name}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500 space-y-1">
                        <div className="flex items-center">
                          <FiPhone className="mr-1" size={14} />
                          {entry.mobileNumber}
                        </div>
                        {entry.email && entry.email !== 'N/A' && (
                          <div className="flex items-center">
                            <FiMail className="mr-1" size={14} />
                            {entry.email}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className="text-lg font-semibold text-gray-900">
                        {type === 'quiz' ? entry.score : entry.totalScore}
                      </span>
                      {type === 'quiz' && (
                        <span className="text-sm text-gray-500">
                          /{entry.totalPoints}
                        </span>
                      )}
                    </td>
                    {type === 'classroom' && (
                      <>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className="text-sm text-gray-600">
                            {entry.quizCount}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className={`text-sm font-medium ${
                            entry.averagePercentage >= 80 ? 'text-green-600' :
                            entry.averagePercentage >= 60 ? 'text-yellow-600' :
                            'text-red-600'
                          }`}>
                            {entry.averagePercentage}%
                          </span>
                        </td>
                      </>
                    )}
                    {type === 'quiz' && (
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className={`text-sm font-medium ${
                          entry.percentage >= 80 ? 'text-green-600' :
                          entry.percentage >= 60 ? 'text-yellow-600' :
                          'text-red-600'
                        }`}>
                          {entry.percentage.toFixed(1)}%
                        </span>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Leaderboard;